import { useMemo } from 'react';
import { useCategories } from '../../../hooks/useCategories';
import { useMarketplaceTags } from '../../../hooks/useMarketplaceTags';
import toTitleCase from '../../strings/toTitleCase';
import { WebCourseSearchFilters } from './useCourseSearch';

const toReadableList = (strings: Array<string>): string => {
    if (strings.length === 1) {
        return strings[0];
    }
    if (strings.length === 2) {
        return `${strings[0]} and ${strings[1]}`;
    }
    const leadingItems = strings.slice(0, strings.length - 1);
    const lastItem = strings[strings.length - 1];
    return `${leadingItems.join(', ')}, and ${lastItem}`;
};

export const useCourseFilterName = (
    filters: WebCourseSearchFilters,
    categorySearchText?: string
): string => {
    const { data: categoriesData } = useCategories({
        category_refs: [],
        category_slugs: filters.category_slug || [],
    });
    const categoryName = useMemo(() => {
        if (categorySearchText) {
            return categorySearchText;
        }
        if (categoriesData && categoriesData[0]) {
            return categoriesData[0].name;
        }
    }, [categorySearchText, categoriesData]);

    const { data: marketplaceTagsData } = useMarketplaceTags({
        tag_refs: [],
        tag_slugs: filters.marketplace_tag_slug || [],
    });
    const tagsName = useMemo(() => {
        if (!marketplaceTagsData || !marketplaceTagsData.length) {
            return undefined;
        }
        return toReadableList(marketplaceTagsData.map((tag) => tag.name));
    }, [marketplaceTagsData]);

    const deliveryName = useMemo(() => {
        if (!filters.delivery || !filters.delivery.length) {
            return undefined;
        }
        if (filters.delivery === 'in-person') {
            return undefined;
        }
        return toTitleCase(filters.delivery);
    }, [filters]);

    const location = useMemo(() => {
        if (!filters.location) {
            return undefined;
        }
        if ('known_location' in filters.location) {
            return `in ${filters.location.known_location}`;
        } else {
            return `near ${filters.location.query}`;
        }
    }, [filters]);

    const filterName = useMemo((): string => {
        const withCategoryName = categoryName ? `${categoryName} courses` : 'Courses';
        const withTags = tagsName ? `${withCategoryName} about ${tagsName}` : withCategoryName;
        const withLocation = location ? `${withTags} ${location}` : withTags;
        const withDeliveryName = deliveryName
            ? `${withLocation} delivered ${deliveryName}`
            : withLocation;
        return withDeliveryName;
    }, [categoryName, deliveryName, location, tagsName]);

    return filterName;
};
